/**
 * Session-related error codes originating in teserver and relayed by
 * tecore-server.
 *
 * The -120..-128 range only appears when teserver runs with
 * `useExtendedErrorsForCore` enabled. Until that flag is turned on, every one
 * of -120..-126 arrives as -109 instead, so both shapes have to work.
 *
 * Kept in sync by hand with the copy in tecore-server, which in turn mirrors
 * API_ResponseClientDatabaseManagerUtils.cpp in teserver.
 */

export const SessionErrorCode = {
    /** Invalid session id, cause unknown (or extended codes disabled). */
    INVALID_SESSION: -109,
    /** teserver was restarted and dropped this session. */
    SERVER_STARTUP: -120,
    /** Session closed due to inactivity. */
    CLOSE_LOGIN_INFO: -121,
    /** Session closed because a newer session was created for this user. */
    CREATE_SESSION: -122,
    /** The user logged out of core. */
    SAVE_LOGOUT_INFO: -123,
    /** The "log out all users" API action was triggered. */
    LOGOUT_ALL_USERS: -124,
    /** The "remove user private info" action was triggered. */
    REMOVE_USER_PRIVATE_INFO: -125,
    /** The "close all user sessions" action was triggered. */
    CLOSE_USER_SESSIONS: -126,
    /** The session renewal failed outright. */
    NEW_SESSION_FAILED: -127,
    /** A session renewal is running and had not finished in time. */
    PENDING_OTHER_PROCESS: -128,
} as const;

/**
 * What to do about a session error.
 *
 * - `renewing`  tecore-server is renewing the session; hold off and retry.
 * - `reload`    the session is gone but the user can be signed back in by
 *               reloading, which re-runs the SSO login.
 * - `terminal`  nothing recovers this; the user has to log in again.
 */
export const SessionAction = {
    RENEWING: "renewing",
    RELOAD: "reload",
    TERMINAL: "terminal",
} as const;

export type TSessionAction = (typeof SessionAction)[keyof typeof SessionAction];

/** The Language key for the message shown to the user, per code. */
const MESSAGE_KEY_BY_CODE = {
    [SessionErrorCode.INVALID_SESSION]: "nc_server_has_ended_session.",
    [SessionErrorCode.SERVER_STARTUP]: "nc_session_ended_server_restarted",
    [SessionErrorCode.CLOSE_LOGIN_INFO]: "nc_session_ended_inactivity",
    [SessionErrorCode.CREATE_SESSION]: "nc_session_ended_newer_session",
    [SessionErrorCode.SAVE_LOGOUT_INFO]: "nc_session_ended_logged_out",
    [SessionErrorCode.LOGOUT_ALL_USERS]: "nc_session_ended_all_users_logged_out",
    [SessionErrorCode.REMOVE_USER_PRIVATE_INFO]: "nc_session_ended_private_info_removed",
    [SessionErrorCode.CLOSE_USER_SESSIONS]: "nc_session_ended_sessions_closed",
    [SessionErrorCode.NEW_SESSION_FAILED]: "nc_session_renewal_failed",
    [SessionErrorCode.PENDING_OTHER_PROCESS]: "nc_session_renewal_failed",
} as const;

type TSessionMessageKey = (typeof MESSAGE_KEY_BY_CODE)[keyof typeof MESSAGE_KEY_BY_CODE];

/**
 * Falls back to the generic message for anything unmapped. That is reachable: -112 is a
 * session error tecore-server can report as still renewing, and if the client runs out of
 * retries it ends the session carrying that code, which has no message of its own.
 */
export const getSessionMessageKey = (code: number): TSessionMessageKey =>
    MESSAGE_KEY_BY_CODE[code] ?? "nc_server_has_ended_session.";

type TErrorBody = {
    /** The full message text, for logging. */
    message: string;
    /** The error code teserver reported, when we could determine it. */
    apiCode: number | null;
    /** Present only for session errors. */
    sessionAction: TSessionAction | null;
};

const SESSION_ACTIONS: readonly TSessionAction[] = Object.values(SessionAction);

const isSessionAction = (value: unknown): value is TSessionAction =>
    SESSION_ACTIONS.indexOf(value as TSessionAction) !== -1;

/**
 * tecore-server answers errors with a JSON body carrying the code and the
 * action. Versions predating that answer with the bare message text, which
 * still contains the code in parentheses, so fall back to reading it from
 * there. The first code in the text is the error itself; any later one belongs
 * to the "Cause:" suffix.
 */
export const parseErrorBody = (body: string): TErrorBody => {
    try {
        const parsed = JSON.parse(body);
        if (parsed && typeof parsed === "object") {
            return {
                message: typeof parsed.message === "string" ? parsed.message : body,
                apiCode: typeof parsed.apiCode === "number" ? parsed.apiCode : null,
                sessionAction: isSessionAction(parsed.sessionAction) ? parsed.sessionAction : null,
            };
        }
    } catch {
        // Not JSON - an older tecore-server. Fall through to the text form.
    }

    const match = /\((-\d+)\)/.exec(body);
    return {
        message: body,
        apiCode: match === null ? null : Number(match[1]),
        // Older tecore-server only ever surfaced -109 as an unrecoverable
        // session error; everything else it retried or reported as a plain
        // request failure.
        sessionAction:
            match !== null && Number(match[1]) === SessionErrorCode.INVALID_SESSION
                ? SessionAction.TERMINAL
                : null,
    };
};
